import { useState, useEffect, useCallback } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from '../../contexts/LanguageContext'
import { useAuth } from '../../contexts/AuthContext'
import { usePublishCV } from '../../hooks/usePublishCV'
import { slugify } from '../../utils/slugify'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { AuthModal } from '../auth/AuthModal'
import type { CV } from '../../types/cv'
import type { TemplateType } from '../../hooks/usePdfGenerator'

interface PublishModalProps {
  isOpen: boolean
  onClose: () => void
  cv: CV
  cvId: string | null
  template: TemplateType
  colorPalette: string
}

export function PublishModal({ isOpen, onClose, cv, cvId, template, colorPalette }: PublishModalProps) {
  const { user } = useAuth()
  const { t } = useTranslation()
  const { publicCV, isPublished, isPublishing, publish, update, unpublish, checkSlug } = usePublishCV(cvId)
  const [slug, setSlug] = useState('')
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null)
  const [isChecking, setIsChecking] = useState(false)
  const [showAuth, setShowAuth] = useState(false)
  const [copied, setCopied] = useState(false)

  // Generate default slug from name
  useEffect(() => {
    if (!isPublished && cv.personalInfo?.fullName) {
      setSlug(slugify(cv.personalInfo.fullName))
    }
    if (isPublished && publicCV) {
      setSlug(publicCV.slug)
    }
  }, [cv.personalInfo?.fullName, isPublished, publicCV])

  // Check slug availability (debounced)
  useEffect(() => {
    if (!slug || isPublished) {
      setSlugAvailable(null)
      return
    }

    const timer = setTimeout(async () => {
      setIsChecking(true)
      const available = await checkSlug(slug)
      setSlugAvailable(available)
      setIsChecking(false)
    }, 500)

    return () => clearTimeout(timer)
  }, [slug, isPublished, checkSlug])

  const publicUrl = `cv-today.com/cv/${slug}`

  const handlePublish = async () => {
    if (!user) {
      setShowAuth(true)
      return
    }
    const result = await publish(cv, template, colorPalette, slug)
    if (result) {
      toast.success(t.publish.published)
    }
  }

  const handleUpdate = async () => {
    await update(cv, template, colorPalette)
    toast.success(t.publish.updated)
  }

  const handleUnpublish = async () => {
    await unpublish()
    toast.success(t.publish.unpublished)
  }

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(`https://${publicUrl}`)
      setCopied(true)
      toast.success(t.common.copied)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback
    }
  }, [publicUrl, t])

  const handleShareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${t.publish.shareMessage} https://${publicUrl}`)}`, '_blank')
  }

  const handleShareLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://${publicUrl}`)}`, '_blank')
  }

  const handleShareEmail = () => {
    const name = cv.personalInfo?.fullName || 'CV'
    window.open(`mailto:?subject=${encodeURIComponent(`${name} - CV`)}&body=${encodeURIComponent(`${t.publish.shareMessage} https://${publicUrl}`)}`)
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">{t.previewPane.share}</h2>
              <p className="text-sm text-slate-500">
                {isPublished ? publicUrl : t.publish.sharePublicLink}
              </p>
            </div>
          </div>

          {/* Slug input */}
          {!isPublished && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">{t.publish.cvUrl}</label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500 flex-shrink-0">cv-today.com/cv/</span>
                <Input
                  value={slug}
                  onChange={(e) => setSlug(slugify(e.target.value))}
                  placeholder="tu-nombre"
                  className="flex-1"
                />
              </div>
              {isChecking && (
                <p className="text-xs text-slate-400 mt-1">{t.publish.checking}</p>
              )}
              {slugAvailable === true && !isChecking && slug && (
                <p className="text-xs text-emerald-600 mt-1">{t.publish.available}</p>
              )}
              {slugAvailable === false && !isChecking && slug && (
                <p className="text-xs text-red-500 mt-1">{t.publish.unavailable}</p>
              )}
            </div>
          )}

          {/* Published state */}
          {isPublished && publicCV && (
            <div className="space-y-4">
              {/* URL + Copy */}
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-700 truncate flex-1">{publicUrl}</span>
                <button
                  onClick={handleCopyLink}
                  className="px-3 py-1.5 text-xs font-medium bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors flex-shrink-0"
                >
                  {copied ? t.common.copied : t.publish.copyLink}
                </button>
              </div>

              {/* View count */}
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span>{publicCV.viewCount} {t.publish.views}</span>
              </div>

              {/* Share buttons */}
              <div className="flex gap-2">
                <button
                  onClick={handleShareWhatsApp}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#25D366] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  WhatsApp
                </button>
                <button
                  onClick={handleShareLinkedIn}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#0A66C2] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  LinkedIn
                </button>
                <button
                  onClick={handleShareEmail}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  Email
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 mt-6">
            {!isPublished ? (
              <Button
                onClick={handlePublish}
                isLoading={isPublishing}
                disabled={isPublishing || (!slugAvailable && !!slug)}
                className="flex-1"
              >
                {t.publish.publishCV}
              </Button>
            ) : (
              <>
                <Button onClick={handleUpdate} isLoading={isPublishing} className="flex-1">
                  {t.publish.update}
                </Button>
                <Button onClick={handleUnpublish} variant="ghost" className="text-red-500 hover:bg-red-50">
                  {t.publish.unpublish}
                </Button>
              </>
            )}
          </div>
        </div>
      </Modal>

      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        initialView="register"
        title={t.publish.registerToPublish}
        description={t.publish.needAccountToPublish}
      />
    </>
  )
}
